'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Heading } from '@/components/ui/Heading';
import { Text } from '@/components/ui/Text';
import styles from './UseCasesCarousel.module.css';

interface UseCase {
  title: string;
  description: string;
}

interface UseCasesCarouselProps {
  useCases: UseCase[];
  autoPlayInterval?: number;
}

export default function UseCasesCarousel({ 
  useCases, 
  autoPlayInterval = 7000 
}: UseCasesCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFirstCycleComplete, setIsFirstCycleComplete] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const slideCountRef = useRef<number>(0);

  const nextSlide = useCallback(() => {
    slideCountRef.current += 1;
    
    // Mark first cycle as complete after 2 slides
    if (slideCountRef.current >= 2) {
      setIsFirstCycleComplete(true);
    }
    
    setActiveIndex((prev) => (prev + 1) % useCases.length);
    setProgressKey((prev) => prev + 1);
  }, [useCases.length]);

  const prevSlide = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + useCases.length) % useCases.length);
    setProgressKey((prev) => prev + 1);
  }, [useCases.length]);

  const goToSlide = useCallback((index: number) => {
    setActiveIndex(index);
    setProgressKey((prev) => prev + 1);
  }, []);

  // Initialize on mount with extra delay for stability
  useEffect(() => {
    // Double requestAnimationFrame to ensure everything is painted
    animationFrameRef.current = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Small additional delay to let browser settle
        setTimeout(() => {
          setIsInitialized(true);
        }, 100);
      });
    });

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Auto-play with precise timing control
  useEffect(() => {
    // Don't start until initialized
    if (!isInitialized) return;

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Don't start new timer if paused
    if (isPaused) return;
    
    // Record start time for this slide
    startTimeRef.current = Date.now();
    
    // For first 2 slides, add small buffer to ensure smooth animation start
    const isEarlySlide = slideCountRef.current < 2;
    const adjustedInterval = isEarlySlide ? autoPlayInterval + 50 : autoPlayInterval;
    
    // Start new timer - this will fire exactly when animation completes
    timerRef.current = setTimeout(() => {
      // Verify that enough time has passed (prevents early firing)
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = adjustedInterval - elapsed;
      
      if (remaining > 100) {
        // Timer fired too early, reschedule
        timerRef.current = setTimeout(() => {
          requestAnimationFrame(() => {
            nextSlide();
          });
        }, remaining);
      } else {
        // Timer is on time, proceed
        requestAnimationFrame(() => {
          nextSlide();
        });
      }
    }, adjustedInterval);

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [activeIndex, isPaused, nextSlide, autoPlayInterval, isInitialized]);

  // Touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    
    if (distance > minSwipeDistance) {
      nextSlide();
    } else if (distance < -minSwipeDistance) {
      prevSlide();
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Get icon based on use case title
  const getUseCaseIcon = (title: string) => {
    const lowerTitle = title.toLowerCase();
    
    // QR ordering
    if (lowerTitle.includes('qr') || lowerTitle.includes('scannen')) {
      return <path d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm13 0h3v3h-3v-3zm0 4h3v2h-3v-2zm-2-2h2v2h-2v-2zm2 2h2v2h-2v-2zm-2 2h2v2h-2v-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
    }
    
    // Hotel specific
    else if (lowerTitle.includes('roomservice') || lowerTitle.includes('kamer')) {
      return <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9zm9-4v10m-4-6h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    } else if (lowerTitle.includes('minimarket') || lowerTitle.includes('checkout')) {
      return <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4H6zM3 6h18M16 10a4 4 0 01-8 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    } else if (lowerTitle.includes('restaurant') || lowerTitle.includes('tablet')) {
      return <path d="M3 3h18M3 9h18M3 15h18M12 3v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    } else if (lowerTitle.includes('wellness') || lowerTitle.includes('pool') || lowerTitle.includes('zwembad')) {
      return <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2m16 0h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41M12 8a4 4 0 100 8 4 4 0 000-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    } else if (lowerTitle.includes('bowling') || lowerTitle.includes('entertainment')) {
      return <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 5v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    } else if (lowerTitle.includes('outlet') || lowerTitle.includes('multi')) {
      return <path d="M4 6h4V4H4v2zm0 5h4V9H4v2zm0 5h4v-2H4v2zm6-10v2h10V6H10zm0 5h10v-2H10v2zm0 5h10v-2H10v2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
    }
    
    // Language/translation
    else if (lowerTitle.includes('taal') || lowerTitle.includes('meertalig') || lowerTitle.includes('language')) {
      return <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 17.93A7.969 7.969 0 014.07 12H7c.03 2.46.68 4.76 1.78 6.59zM7 11H4.07A7.969 7.969 0 0111 4.07V11zm2 0V4.07A7.969 7.969 0 0119.93 11H13zm0 7.93c1.1-1.83 1.75-4.13 1.78-6.59h2.93A7.969 7.969 0 0113 18.93z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
    }
    
    // Money/costs
    else if (lowerTitle.includes('bezorgkosten') || lowerTitle.includes('kosten') || lowerTitle.includes('bedrag') || lowerTitle.includes('minimum')) {
      return <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    }
    
    // Time/scheduling
    else if (lowerTitle.includes('tijdslot') || lowerTitle.includes('tijd') || lowerTitle.includes('openingstijd') || lowerTitle.includes('schema')) {
      return <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 110-16 8 8 0 010 16zm1-8V6h-2v7h6v-2h-4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
    }
    
    // Service requests/notifications
    else if (lowerTitle.includes('service') || lowerTitle.includes('verzoek') || lowerTitle.includes('sms') || lowerTitle.includes('melding')) {
      return <path d="M18 8A6 6 0 106 8c0 7-3 9-3 9h18s-3-2-3-9zm-5.27 13a2 2 0 01-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    }
    
    // Food/dining
    else if (lowerTitle.includes('all-you-can-eat') || lowerTitle.includes('rondes') || lowerTitle.includes('diner')) {
      return <path d="M17 2l4 4-4 4m0 0H7a4 4 0 00-4 4v2m4 6l-4-4 4-4m0 0h10a4 4 0 004-4V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    }
    
    // Health/allergens
    else if (lowerTitle.includes('allergen') || lowerTitle.includes('zwanger') || lowerTitle.includes('dietary')) {
      return <path d="M12 2L2 19.5h20L12 2zm0 4l7 12H5l7-12zm-1 5v4h2v-4h-2zm0 5v2h2v-2h-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
    }
    
    // Kiosk/self-service
    else if (lowerTitle.includes('kiosk') || lowerTitle.includes('doorloop') || lowerTitle.includes('self')) {
      return <path d="M4 6h16M4 10h16M4 14h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    }
    
    // Analytics/tracking
    else if (lowerTitle.includes('analytics') || lowerTitle.includes('tracking') || lowerTitle.includes('real-time')) {
      return <path d="M9 17v-6m3 6V5m3 12v-8M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    }
    
    // Inventory/stock
    else if (lowerTitle.includes('voorraad') || lowerTitle.includes('product') || lowerTitle.includes('management')) {
      return <path d="M4 6h4V4H4v2zm0 5h4V9H4v2zm0 5h4v-2H4v2zm6-10v2h10V6H10zm0 5h10v-2H10v2zm0 5h10v-2H10v2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
    }
    
    // Upselling/recommendations
    else if (lowerTitle.includes('upsell') || lowerTitle.includes('aanbevel') || lowerTitle.includes('suggestie')) {
      return <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    }
    
    // Routing/automation
    else if (lowerTitle.includes('routering') || lowerTitle.includes('automatisch') || lowerTitle.includes('keuken')) {
      return <path d="M12 2l7 19-7-4-7 4 7-19z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    }
    
    // Payment/integration
    else if (lowerTitle.includes('integratie') || lowerTitle.includes('kassa') || lowerTitle.includes('pin') || lowerTitle.includes('pos')) {
      return <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    }
    
    // Events/promotions
    else if (lowerTitle.includes('promotie') || lowerTitle.includes('event') || lowerTitle.includes('banner') || lowerTitle.includes('special')) {
      return <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82zM7 7h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    }
    
    // Seasonal/calendar
    else if (lowerTitle.includes('seizoen') || lowerTitle.includes('menu')) {
      return <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM12 13a2 2 0 100 4 2 2 0 000-4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
    }
    
    // Reservations
    else if (lowerTitle.includes('reserv') || lowerTitle.includes('booking')) {
      return <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14l-2-2-1 1 3 3 7-7-1-1-6 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
    }
    
    // Beach specific
    else if (lowerTitle.includes('zone') || lowerTitle.includes('ligbed') || lowerTitle.includes('bed')) {
      return <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
    } else if (lowerTitle.includes('cocktail') || lowerTitle.includes('drink') || lowerTitle.includes('presentatie')) {
      return <path d="M9 3L5 9h14l-4-6M12 9v12m-4 0h8M9 15l3-3 3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    } else if (lowerTitle.includes('vip') || lowerTitle.includes('premium')) {
      return <path d="M12 2l3.5 7 5.5-4-2 10H5L3 5l5.5 4L12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    } else if (lowerTitle.includes('watersport') || lowerTitle.includes('activiteit')) {
      return <path d="M3 12c.55-.59 1.22-.96 2-1.11.78-.15 1.57-.05 2.25.24.68.29 1.23.77 1.67 1.37M15 12c.55-.59 1.22-.96 2-1.11.78-.15 1.57-.05 2.25.24.68.29 1.23.77 1.67 1.37M3 18c.55-.59 1.22-.96 2-1.11.78-.15 1.57-.05 2.25.24.68.29 1.23.77 1.67 1.37M15 18c.55-.59 1.22-.96 2-1.11.78-.15 1.57-.05 2.25.24.68.29 1.23.77 1.67 1.37" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    } else if (lowerTitle.includes('branding') || lowerTitle.includes('custom') || lowerTitle.includes('huisstijl')) {
      return <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10c0 3.314-1.343 6.314-3.515 8.485l-1.415-1.414C18.844 17.298 20 14.763 20 12c0-4.418-3.582-8-8-8s-8 3.582-8 8 3.582 8 8 8v2zm-3-7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm3-4a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm3 4a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
    } else if (lowerTitle.includes('centraal') || lowerTitle.includes('central')) {
      return <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    } else if (lowerTitle.includes('terras') || lowerTitle.includes('terrace')) {
      return <path d="M3 12h18M3 6h18M8 12v10m8-10v10M3 22h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>;
    }
    
    // Default checkmark
    return <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
  };

  // Calculate position for each card
  const getCardStyle = (index: number) => {
    const diff = index - activeIndex;
    const totalCards = useCases.length;
    
    // Normalize diff to be between -totalCards/2 and totalCards/2
    let normalizedDiff = diff;
    if (normalizedDiff > totalCards / 2) {
      normalizedDiff -= totalCards;
    } else if (normalizedDiff < -totalCards / 2) {
      normalizedDiff += totalCards;
    }
    
    const isActive = diff === 0;
    const absDistance = Math.abs(normalizedDiff);
    
    // Calculate transform values
    const translateX = normalizedDiff * 110; // spacing between cards in %
    const translateZ = isActive ? 0 : -100 - (absDistance * 50); // depth
    const scale = isActive ? 1 : Math.max(0.7 - (absDistance * 0.1), 0.5);
    const opacity = isActive ? 1 : Math.max(0.3 - (absDistance * 0.15), 0);
    const rotateY = normalizedDiff * -5; // subtle rotation
    
    return {
      transform: `translateX(${translateX}%) translateZ(${translateZ}px) scale(${scale}) rotateY(${rotateY}deg)`,
      opacity,
      zIndex: isActive ? 10 : Math.max(5 - absDistance, 0),
      pointerEvents: isActive ? 'auto' : 'none',
    } as React.CSSProperties;
  };

  return (
    <div className={styles.carouselWrapper}>
      <div 
        className={styles.carouselContainer}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className={styles.carouselTrack}>
          {useCases.map((useCase, index) => (
            <div
              key={index}
              className={`${styles.carouselCard} ${index === activeIndex ? styles.active : ''}`}
              style={getCardStyle(index)}
            >
              <div className={styles.cardIcon}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {getUseCaseIcon(useCase.title)}
                </svg>
              </div>
              <Heading as="h3" className={styles.cardTitle}>
                {useCase.title}
              </Heading>
              <Text size="base" color="secondary" className={styles.cardDescription}>
                {useCase.description}
              </Text>
              {index === activeIndex && isInitialized && (
                <div 
                  key={`progress-${progressKey}-${activeIndex}`}
                  className={styles.progressBar}
                  style={{
                    animationDuration: `${slideCountRef.current < 2 ? autoPlayInterval + 50 : autoPlayInterval}ms`,
                    animationPlayState: isPaused ? 'paused' : 'running'
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Navigation arrows */}
        <button
          className={styles.navArrowLeft}
          onClick={prevSlide}
          aria-label="Previous slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button
          className={styles.navArrowRight}
          onClick={nextSlide}
          aria-label="Next slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Counter */}
      <div className={styles.counter}>
        {activeIndex + 1} / {useCases.length}
      </div>
    </div>
  );
}


